import {
  FC,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTheme } from 'styled-components';
import { SatoshiSVG } from '~/modules/satoshi/components/SatoshiSVG';
import { usePrivateLayout } from '~/layouts/private/providers/privateLayout';

import customAxios from '~/common/services/customAxios';
import { useToast } from '~/common/providers/toast';
import { useAuth } from '~/common/providers/auth';
import { SPACING } from '~/common/layout-propertis/spacing';
import { useMaxHeightToBottom } from '~/common/hooks/use-max-height-to-bottom';
import { UserSubscriptionTypes } from '~/common/enums/UserSubscriptionTypes';
import { allowNewSubscriptions } from '~/common/utils/allow-new-subscriptions';
import { Typography } from '~/common/components/Typography';
import { ProgressBar } from '~/common/components/ProgressBar';
import { Button } from '~/common/components/Button';
import { AlertInfo } from '~/common/components/AlertInfo';

import {
  BarContainer,
  SatoshiBody,
  SatoshiContainer,
  SatoshiFooter,
  SatoshiHeader,
} from './styles';
import { ResultStep } from './ResultStep';
import { OptionsStep } from './OptionsStep';
import { LoadingStep } from './Loading';
import { CoinStep } from './CoinStep';
import { ISatoshiResponse } from '../../interfaces/ISatoshiResponse';
import { SatoshiOptionEnum } from '../../enums/SatoshiOptionEnum';

type TSatoshiSteps = 'option' | 'coin' | 'loading' | 'result';

interface ISatoshiContentProps {
  close: () => void;
}
export const SatoshiContent: FC<ISatoshiContentProps> = ({ close }) => {
  const { isMobile } = usePrivateLayout();
  const {
    loadUser,
    satoshiUsagePercentage,
    user: { freeTrial, subscription: userType } = {},
  } = useAuth();

  const id = useId();
  const theme = useTheme();
  const { showToast } = useToast();
  const resultRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<TSatoshiSteps>('option');
  const [coin, setCoin] = useState<string>(null);
  const [responding, setResponding] = useState<boolean>(false);
  const [typeIndex, setTypeIndex] = useState<number>(0);
  const [option, setOption] = useState<SatoshiOptionEnum>(
    SatoshiOptionEnum['coinanalysis']
  );
  const [response, setResponse] = useState<ISatoshiResponse>(
    {} as ISatoshiResponse
  );

  const { height: bodyHeight } = useMaxHeightToBottom({
    elementId: id,
    initialHeight: 200,
  });
  const canShowSubscriptionCTA = allowNewSubscriptions();

  const copyResponse = (): void => {
    navigator.clipboard.writeText(response.message as string);

    showToast({
      type: 'success',
      position: 'top',
      description: 'O texto foi copiado para sua área de transferência!',
    });
  };

  const proceed = useCallback(async () => {
    setStep('loading');
    try {
      const { data: satoshiResponse } = await customAxios.get(
        'Satoshi/analysis',
        {
          params: {
            type: option,
            coinId: coin ? Number(coin) : undefined,
          },
        }
      );

      loadUser();
      setResponse(satoshiResponse);
      setResponding(true);
      setStep('result');
    } catch {
      setStep('option');
      showToast({
        type: 'error',
        description:
          'Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente.',
      });
    }
  }, [coin, loadUser, option, showToast]);

  const finish = useCallback(() => {
    setStep('option');
    setCoin(null);
    setResponding(false);
    setOption(SatoshiOptionEnum['coinanalysis']);
    close();
  }, [close]);

  const handleNextClick = useCallback(() => {
    if (step === 'option') {
      if (option === SatoshiOptionEnum['coinanalysis']) {
        setStep('coin');
      } else {
        proceed();
      }
    } else if (step === 'coin') {
      proceed();
    } else if (step === 'result') {
      finish();
    }
  }, [step, option, proceed, finish]);

  const nextDisabled = useMemo(() => {
    if (step === 'option') {
      return !option;
    }
    if (step === 'coin') {
      return !coin;
    }
    return false;
  }, [step, coin, option]);

  useEffect(() => {
    if (step === 'result') {
      resultRef.current.scrollTo({
        top: resultRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [typeIndex, step]);

  const renderSatoshiWarningText = useCallback((): string | JSX.Element => {
    if (freeTrial) {
      return canShowSubscriptionCTA
        ? 'Você está usando a versão teste do Satoshi AI, com um limite reduzido de consultas. Quer ir além? Desbloqueie mais consultas assinando o plano Platinum.'
        : 'Você está usando a versão teste do Satoshi AI, com um limite reduzido de consultas. No momento, novas assinaturas estão indisponíveis.';
    }

    if (userType === UserSubscriptionTypes.platinum) {
      return 'Creditos excedidos, por favor, aguarde até o próximo dia para continuar usando o Satoshi AI.';
    }

    if (userType === UserSubscriptionTypes.free) {
      return (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: SPACING._8 }}
        >
          <Typography.Paragraph size="small" color="neutral.0">
            Este recurso é exclusivo para assinantes do plano Platinum. No
            momento, você está no plano gratuito e não possui acesso ao Satoshi
            AI.
          </Typography.Paragraph>

          {canShowSubscriptionCTA ? (
            <Typography.Paragraph size="small" color="neutral.0">
              <b>Quer destravar todo o poder da nossa inteligência artificial?</b>{' '}
              Com o plano Platinum, você libera funcionalidades exclusivas que
              podem transformar sua experiência. Assine agora mesmo!
            </Typography.Paragraph>
          ) : (
            <Typography.Paragraph size="small" color="neutral.0">
              No momento, novas assinaturas estão indisponíveis e o acesso a
              esta funcionalidade permanece restrito a usuários com plano
              Platinum ativo.
            </Typography.Paragraph>
          )}
        </div>
      );
    }
  }, [canShowSubscriptionCTA, freeTrial, userType]);

  return (
    <SatoshiContainer>
      <SatoshiHeader visible={['option', 'coin', 'result'].includes(step)}>
        <SatoshiSVG />

        <Typography.Paragraph
          size="x-large"
          color="neutral.0"
          style={{ fontWeight: 'bold' }}
        >
          Satoshi AI
        </Typography.Paragraph>

        <Typography.Paragraph size="medium" color="neutral.200">
          A inteligência artificial voltada para o mercado de criptomoedas que
          simplifica tudo.
        </Typography.Paragraph>

        {userType === UserSubscriptionTypes.standard ? (
          <AlertInfo
            color="neutral.500"
            info={
              canShowSubscriptionCTA
                ? 'Você está no plano Standard, que não inclui o Satoshi AI. Para desbloquear, assine o plano Platinum.'
                : 'Você está no plano Standard, que não inclui o Satoshi AI. No momento, novas assinaturas estão indisponíveis.'
            }
          />
        ) : (
          <>
            <BarContainer>
              <ProgressBar.LinearBar
                color="neutral"
                percentage={satoshiUsagePercentage}
              />
            </BarContainer>

            {satoshiUsagePercentage >= 100 && (
              <AlertInfo color="orange.500" info={renderSatoshiWarningText()} />
            )}
          </>
        )}
      </SatoshiHeader>

      <SatoshiBody
        ref={resultRef}
        id={id}
        style={{
          height: `${bodyHeight}px`,
          maxHeight: `${bodyHeight}px`,
        }}
      >
        <OptionsStep
          option={option}
          setOption={setOption}
          visible={step === 'option'}
        />
        <CoinStep coin={coin} setCoin={setCoin} visible={step === 'coin'} />
        <LoadingStep visible={step === 'loading'} />
        <ResultStep
          visible={step === 'result'}
          result={response}
          onType={() => setTypeIndex((prev) => prev + 1)}
          onFinish={() => setResponding(false)}
        />
      </SatoshiBody>

      <SatoshiFooter visible={['option', 'coin', 'result'].includes(step)}>
        {step === 'coin' && (
          <Button
            width="100%"
            text="Voltar"
            icon="arrow-left-s-line"
            justifyContent="center"
            backgroundColor={theme.neutral[950]}
            textOrIconColor={theme.green[500]}
            borderColor={theme.green[500]}
            onClick={() => setStep('option')}
          />
        )}

        {step === 'result' && (
          <Button
            width="100%"
            icon="file-copy-line"
            disabled={responding}
            loading={responding}
            loader="bar"
            text={responding ? 'Respondendo...' : 'Copiar'}
            justifyContent="center"
            backgroundColor={theme.neutral[950]}
            textOrIconColor={theme.green[500]}
            borderColor={theme.green[500]}
            loaderColor="green.500"
            onClick={() =>
              step === 'result' ? copyResponse() : setStep('option')
            }
          />
        )}

        <Button
          width="100%"
          text={step === 'result' ? 'Finalizar' : 'Continuar'}
          justifyContent="center"
          onClick={handleNextClick}
          disabled={
            nextDisabled ||
            (step === 'result' && responding) ||
            userType === UserSubscriptionTypes.standard ||
            satoshiUsagePercentage >= 100
          }
        />
      </SatoshiFooter>

      {isMobile && (
        <Button
          text="Fechar"
          width="100%"
          onClick={close}
          justifyContent="center"
          backgroundColor={theme.neutral[950]}
          textOrIconColor={theme.green[500]}
        />
      )}
    </SatoshiContainer>
  );
};
